/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import * as React from "react";
type LabelStrategy = (fieldName: string) => string;
function generateLabel(fieldName: string, labelStrategy: LabelStrategy | undefined, fallbackLabelStrategy: LabelStrategy): string {
    if (labelStrategy) {
        return labelStrategy(fieldName);
    }
    return fallbackLabelStrategy(fieldName);
}
interface SetLabelStrategyProps {
    labelStrategy: LabelStrategy;
    children?: React.ReactNode;
}
interface LabelStrategyContext {
    labelStrategy: LabelStrategy | undefined;
}
const LabelStrategyContext = React.createContext<LabelStrategyContext>({
    labelStrategy: undefined,
});
/* This component is used to set a "labeling" strategy on the react context.
 * This strategy is used to determine how labels for input components are generated
 * This allows us to do things like say that all inputs within the filter panel
 * should have labels of the form "By [fieldName]", eg "By environments".
 * For components to use the label strategy to generate their label, they should use the `UseLabelStrategy` HOC*/
export function SetLabelStrategy({ labelStrategy, children }: SetLabelStrategyProps) {
    const contextValue = React.useMemo(() => ({
        labelStrategy,
    }), [labelStrategy]);
    return (<LabelStrategyContext.Provider value={contextValue}>
            <div>{children}</div>
        </LabelStrategyContext.Provider>);
}
/* This clears any label strategy that might be on the context (see `SetLabelStrategy`),
 * so that any descendant components won't use that label strategy
 * This is useful for things like dialogs, which are usually placed at some arbitrary point
 * in the component hierarchy because they have to go somewhere, and therefore don't necessarily
 * want to inherit the provided label strategy.*/
export function ResetLabelStrategy({ children }: {
    children?: React.ReactNode;
}) {
    const contextValue = React.useMemo(() => ({
        labelStrategy: undefined,
    }), []);
    return (<LabelStrategyContext.Provider value={contextValue}>
            <div>{children}</div>
        </LabelStrategyContext.Provider>);
}
/* This HOC wraps a component and makes it use the label strategy on the react context in order to generate its label
 * It is mainly used by input components, like `Text` and `Select`
 * In order to provide a label strategy on the context, use `SetLabelStrategy`*/
export default function UseLabelStrategy<Props extends {
    label?: string | JSX.Element;
}>(Comp: React.ComponentClass<Props>, fallbackLabelStrategy: LabelStrategy) {
    type UseLabelStrategyInternalProps = Props & {
        fieldName?: string;
    };
    function UseLabelStrategyInternal(props: UseLabelStrategyInternalProps) {
        const { labelStrategy } = React.useContext(LabelStrategyContext);
        const { fieldName, label, ...other } = props as any;
        const derivedLabel = label || (fieldName ? generateLabel(fieldName, labelStrategy, fallbackLabelStrategy) : undefined);
        return <Comp {...other} label={derivedLabel}/>;
    }
    return UseLabelStrategyInternal;
}
